import { toCanvas } from "html-to-image"

const EXPORT_SELECTOR = "[data-scorecard-export='true']"
const SHARE_SELECTOR = "[data-scorecard-share='true']"
const EXPORT_SIZE = 1440

// 카드마다 규격이 다르다. 카드가 자기 data 속성으로 밝히고, 없으면 정사각(1440)을
// 유지한다 — 기존 호출부를 건드리지 않기 위한 기본값이다.
//
// 높이를 밝히지 않은 카드는 폭만 고정하고 실제 높이를 잰다. 영수증처럼 내용에 따라
// 길이가 변하는 형식이 그렇다 — 높이를 박으면 키워드를 적게 고른 사람의 카드에
// 빈 종이가 딸려 나간다.
function exportSizeFor(target) {
  const width = Number.parseInt(target.dataset.scorecardExportWidth || "", 10)
  const height = Number.parseInt(target.dataset.scorecardExportHeight || "", 10)

  return {
    width: Number.isFinite(width) && width > 0 ? width : EXPORT_SIZE,
    height: Number.isFinite(height) && height > 0 ? height : null
  }
}

// 폭을 고정한 뒤 그 폭에서의 실제 높이를 잰다. cqw로 조판된 카드는 폭이 바뀌면
// 글자 크기도 같이 바뀌므로, 폭을 먼저 걸어놓고 재야 값이 맞는다.
//
// min-height도 함께 걷는다. lockExportSize가 자리표시자로 걸어 둔 값이 남아 있으면
// 그것이 바닥이 되어, 그보다 짧은 카드가 실제보다 크게 측정된다 — 그러면 빈 종이가
// 딸려 나가고, 높이를 재는 이유 자체가 사라진다.
function measureHeight(target, width) {
  const previous = { height: target.style.height, minHeight: target.style.minHeight }
  target.style.height = "auto"
  target.style.minHeight = "0px"
  const measured = Math.ceil(target.getBoundingClientRect().height)
  target.style.height = previous.height
  target.style.minHeight = previous.minHeight
  return measured > 0 ? measured : width
}

function lockExportSize(target, size) {
  const previous = {
    width: target.style.width,
    height: target.style.height,
    maxWidth: target.style.maxWidth,
    minWidth: target.style.minWidth,
    minHeight: target.style.minHeight
  }

  target.style.width = `${size.width}px`
  target.style.height = `${size.height}px`
  target.style.maxWidth = `${size.width}px`
  target.style.minWidth = `${size.width}px`
  target.style.minHeight = `${size.height}px`

  return () => {
    target.style.width = previous.width
    target.style.height = previous.height
    target.style.maxWidth = previous.maxWidth
    target.style.minWidth = previous.minWidth
    target.style.minHeight = previous.minHeight
  }
}

// 저장·공유가 실제로 일어났는지 세는 신호. 실패해도 조용히 넘어간다 — 계측 때문에
// 사용자가 이미 손에 넣은 이미지를 실패로 되돌릴 이유가 없다.
function pingExport(trigger, overrideAction = null) {
  const url = trigger.dataset.scorecardExportPing
  if (!url) return

  const token = document.querySelector("meta[name='csrf-token']")?.content
  const headers = { "Content-Type": "application/json" }
  if (token) headers["X-CSRF-Token"] = token

  fetch(url, {
    method: "POST",
    headers,
    credentials: "same-origin",
    keepalive: true,
    body: JSON.stringify({ share_action: overrideAction || trigger.dataset.scorecardExportPingAction || "save" })
  }).catch(() => {})
}

function timestampLabel() {
  const now = new Date()
  const pad = (value) => String(value).padStart(2, "0")
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
}

async function exportScorecard(trigger) {
  const originalText = trigger.textContent
  trigger.setAttribute("aria-busy", "true")
  trigger.classList.add("pointer-events-none", "opacity-70")
  trigger.textContent = trigger.dataset.exportingLabel || "Exporting..."

  try {
    const canvas = await renderScorecardCanvas(trigger)
    if (!canvas) return false

    const baseName = trigger.dataset.scorecardExportFilename || "tasting-card"
    const blob = await canvasToBlob(canvas)
    if (!blob) return false
    downloadBlob(blob, `${baseName}-${timestampLabel()}.png`)
    pingExport(trigger)
    return true
  } catch (_error) {
    return false
  } finally {
    trigger.textContent = originalText
    trigger.removeAttribute("aria-busy")
    trigger.classList.remove("pointer-events-none", "opacity-70")
  }
}

async function shareScorecard(trigger) {
  const originalText = trigger.textContent
  trigger.setAttribute("aria-busy", "true")
  trigger.classList.add("pointer-events-none", "opacity-70")
  trigger.textContent = trigger.dataset.sharingLabel || "Sharing..."

  try {
    const canvas = await renderScorecardCanvas(trigger)
    if (!canvas) return false

    const baseName = trigger.dataset.scorecardExportFilename || "tasting-card"
    const filename = `${baseName}-${timestampLabel()}.png`
    const blob = await canvasToBlob(canvas)
    if (!blob) return false

    const shared = await tryNativeShare(trigger, blob, filename)
    if (shared) {
      pingExport(trigger)
      return true
    }

    // File share is unavailable on this device/browser; fall back to download.
    // 공유 버튼을 눌렀어도 실제로 일어난 일은 저장이다 — share_action은 그 둘을
    // 가르려고 둔 값이므로, 여기서 "share"로 세면 공유 쪽이 부풀어 오른다.
    downloadBlob(blob, filename)
    pingExport(trigger, "save")
    return true
  } catch (error) {
    if (error?.name === "AbortError") return true
    return false
  } finally {
    trigger.textContent = originalText
    trigger.removeAttribute("aria-busy")
    trigger.classList.remove("pointer-events-none", "opacity-70")
  }
}

async function renderScorecardCanvas(trigger) {
  const targetSelector = trigger.dataset.scorecardExportTarget || "#scorecard-export-target"
  const rootTarget = document.querySelector(targetSelector)
  const target = rootTarget?.querySelector("[data-scorecard-canvas='true']") || rootTarget
  if (!target) return null

  const requested = exportSizeFor(target)
  const unlockSize = lockExportSize(target, { width: requested.width, height: requested.height ?? requested.width })

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready
    }
    await new Promise((resolve) => window.requestAnimationFrame(resolve))

    // 높이 측정은 반드시 폰트가 실린 뒤다. 폴백 글꼴로 재면 브랜드 폰트가 들어오며
    // 줄이 다시 접힐 때 카드가 재놓은 값보다 길어지고, toCanvas는 그 낡은 값으로
    // 잘라 버린다(바코드·워드마크가 날아간다).
    const size = {
      width: requested.width,
      height: requested.height ?? measureHeight(target, requested.width)
    }
    if (requested.height === null) {
      target.style.height = `${size.height}px`
      target.style.minHeight = `${size.height}px`
      // 높이를 방금 바꿨으니 한 프레임 더 준다 — 그래야 toCanvas가 확정된 레이아웃을 본다.
      await new Promise((resolve) => window.requestAnimationFrame(resolve))
    }

    return await toCanvas(target, {
      cacheBust: true,
      pixelRatio: 1,
      width: size.width,
      height: size.height,
      canvasWidth: size.width,
      canvasHeight: size.height
    })
  } finally {
    unlockSize()
  }
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png")
  })
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

async function tryNativeShare(trigger, blob, filename) {
  if (typeof navigator === "undefined") return false
  if (typeof navigator.share !== "function") return false
  if (typeof File !== "function") return false

  const file = new File([blob], filename, { type: "image/png" })
  const payload = {
    files: [ file ],
    title: trigger.dataset.scorecardShareTitle || "Tasting Card",
    text: trigger.dataset.scorecardShareText || ""
  }

  if (typeof navigator.canShare === "function" && !navigator.canShare({ files: payload.files })) {
    return false
  }

  await navigator.share(payload)
  return true
}

function bindScorecardExport() {
  if (window.__scorecardExportBound) return
  window.__scorecardExportBound = true

  document.addEventListener("click", async (event) => {
    const exportTrigger = event.target.closest(EXPORT_SELECTOR)
    if (exportTrigger) {
      event.preventDefault()
      const success = await exportScorecard(exportTrigger)
      if (!success) {
        const message = exportTrigger.dataset.scorecardExportFailedMessage || "Could not export scorecard image."
        window.alert(message)
      }
      return
    }

    const shareTrigger = event.target.closest(SHARE_SELECTOR)
    if (shareTrigger) {
      event.preventDefault()
      const success = await shareScorecard(shareTrigger)
      if (!success) {
        const message = shareTrigger.dataset.scorecardShareFailedMessage || "Could not share summary card image."
        window.alert(message)
      }
    }
  })
}

bindScorecardExport()
